import {TherapySequenceJourneyTimelineInternal} from '@/modules/patients-therapies/components/patient-therapy-sequence-journey-timeline'
import {JourneyStatusStep, JourneyStepStatus} from '@/modules/patients-therapies/hooks'
import {useCan} from '@/shared/hooks/use-permissions'
import {Badge, Box, Button, Checkbox, Icon, Menu, Portal, Status, Table, Text} from '@chakra-ui/react'
import {OverdosePreventionLevel} from '@cypro/engine/database/schema'
import {PatientProtocol, PatientTherapy} from '@cypro/engine/queries'
import {dialog} from '@cypro/ui/components'
import {Tooltip} from '@cypro/ui/components/ui/tooltip'
import {Tolgee} from '@cypro/ui/integrations/tolgee'
import {TFnType} from '@tolgee/react'
import {memo, MouseEvent, useState} from 'react'
import {LuCircleAlert, LuCopy, LuEllipsis, LuPen, LuQuote, LuTriangleAlert, LuX} from 'react-icons/lu'
import {AddInstructionNoteDialog} from '../dialogs/add-instruction-note'

export type TherapyStep = {
  label: string
  step: JourneyStatusStep
  status: JourneyStepStatus
  color: string
}

export type FlatRow =
  | {type: 'date-header'; dateKey: string; isCurrent: boolean}
  | {
      type: 'therapy'
      therapy: PatientTherapy
      medicine: PatientTherapy['medicine'][number]
      medicineIndex: number
      timeLabel?: string | null
      overdosePreventionLevel?: OverdosePreventionLevel
      limit?: string
      hasObservations?: boolean
      medication?: string
      medicationAmount?: string
      medicationProportion?: string
      calculationMethodLabel?: string
      calculationValueLabel?: string
      solution?: string
      solutionVolume?: string
      steps?: TherapyStep[]
      isCurrent: boolean
    }
  | {type: 'medicine-note'; therapyId: string; medicineId: string; notes: string; isCurrent: boolean}
  | {type: 'note'; therapy: PatientTherapy; timeLabel?: string | null; isCurrent: boolean}

const formatDateKey = (value: number, locale: string) =>
  new Intl.DateTimeFormat(locale, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(new Date(value))

function getOverDosePreventionIcon(level?: OverdosePreventionLevel) {
  switch (level) {
    case OverdosePreventionLevel.enum.HARD_STOP:
      return {icon: LuTriangleAlert, color: 'red.500'}
    case OverdosePreventionLevel.enum.SOFT_WARNING:
      return {icon: LuCircleAlert, color: 'orange.500'}
    case OverdosePreventionLevel.enum.INFO:
      return {icon: LuCircleAlert, color: 'blue.500'}
    default:
      return null
  }
}

function TherapyProtocolInfo({therapy, t}: {therapy: PatientTherapy; t: TFnType}) {
  return (
    <Box display={'flex'} flexDirection={'column'}>
      <Tooltip content={therapy.protocol?.name}>
        <Text
          fontSize="sm"
          width="100%"
          fontWeight="medium"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
          overflow="hidden"
          lineHeight="20px"
        >
          {therapy.protocol?.name || '-'}
        </Text>
      </Tooltip>
      {therapy.cycle !== null && therapy.day !== null && (
        <Text color="fg.muted" fontSize="xs" fontWeight="medium" lineHeight="1rem">
          {t('c_x_d_y', {cycle: therapy.cycle, day: therapy.day})}
        </Text>
      )}
    </Box>
  )
}

interface NoteTherapyRowProps {
  therapy: PatientTherapy
  timeLabel?: string | null
  isCurrent?: boolean
  isSelected?: boolean
  onToggleSelection?: (shift: boolean) => void
  patientProtocols: PatientProtocol[]
  onDelete?: (params: {id: string}) => void
  onRowClick: (event: MouseEvent, therapy: PatientTherapy) => void
  t: TFnType
}

const NoteTherapyRow = memo(function NoteTherapyRow({
  therapy,
  timeLabel,
  isCurrent,
  isSelected,
  onToggleSelection,
  patientProtocols,
  onDelete,
  t,
}: NoteTherapyRowProps) {
  const canEditPrescriptions = useCan('prescriptions.edit')

  async function onClickEdit() {
    await dialog.open('add-instruction-note', {
      children: (
        <AddInstructionNoteDialog
          patientProtocols={patientProtocols}
          patientId={therapy.patientId}
          existing={therapy}
        />
      ),
    })
  }

  async function onClickDuplicate() {
    await dialog.open('add-instruction-note', {
      children: (
        <AddInstructionNoteDialog
          patientProtocols={patientProtocols}
          patientId={therapy.patientId}
          existing={therapy}
          duplicate={true}
        />
      ),
    })
  }

  function onClickDelete() {
    onDelete?.({id: therapy.id})
  }

  return (
    <>
      <Table.Cell
        paddingBlock="1"
        border="none"
        bg={'bg.warning'}
        borderLeft={isCurrent ? '2px solid #0891B2' : 'none'}
      >
        {/* a shift-click is left to the row, which selects the range; the box's own toggle is cancelled */}
        <Box
          onClickCapture={event => {
            if (event.shiftKey) event.preventDefault()
          }}
          onClick={event => {
            if (!event.shiftKey) event.stopPropagation()
          }}
        >
          <Checkbox.Root
            size="md"
            aria-label={t('select_row')}
            checked={Boolean(isSelected)}
            onCheckedChange={() => onToggleSelection?.(false)}
          >
            <Checkbox.HiddenInput />
            <Checkbox.Control />
          </Checkbox.Root>
        </Box>
      </Table.Cell>

      <Table.Cell paddingBlock="1" border="none" bg={'bg.warning'}></Table.Cell>

      <Table.Cell paddingBlock="1" border="none" bg={'bg.warning'}>
        <Box display={'flex'} flexDirection={'column'} gap={'4px'}>
          <Text fontWeight="normal" fontSize="sm" color="fg">
            {therapy.time}
          </Text>
          {timeLabel && (
            <Text fontWeight="medium" fontSize="xs" color={'fg.muted'}>
              {timeLabel}
            </Text>
          )}
        </Box>
      </Table.Cell>
      <Table.Cell paddingBlock="1" border="none" bg={'bg.warning'}>
        <TherapyProtocolInfo therapy={therapy} t={t} />
      </Table.Cell>
      <Table.Cell paddingBlock="1" border="none" colSpan={6} bg={'bg.warning'}>
        {therapy.note && (
          <Box display={'flex'} flexDirection={'row'} gap={2}>
            <LuQuote color="black" />
            <Text fontWeight="medium" fontStyle="italic" fontSize="sm" color="fg.muted">
              {therapy?.note}
            </Text>
          </Box>
        )}
      </Table.Cell>
      <Table.Cell paddingBlock="1" border="none" bg={'bg.warning'}>
        <Menu.Root>
          <Menu.Trigger asChild>
            <Button size="md" variant="plain" paddingInline="0">
              <LuEllipsis />
            </Button>
          </Menu.Trigger>
          <Portal>
            <Menu.Positioner>
              <Menu.Content>
                <Menu.Item value="edit-note" onClick={onClickEdit}>
                  <Box display="flex" flexDirection="row" alignItems={'center'} gap="2">
                    <LuPen />
                    <Text>{t('therapy_sequence.open')}</Text>
                  </Box>
                </Menu.Item>
                {canEditPrescriptions && (
                  <Menu.Item value="duplicate-note" onClick={onClickDuplicate}>
                    <Box display="flex" flexDirection="row" alignItems={'center'} gap="2">
                      <LuCopy />
                      <Text>{t('therapy_sequence.duplicate')}</Text>
                    </Box>
                  </Menu.Item>
                )}
                {canEditPrescriptions && (
                  <Menu.Item value="delete-note" onClick={onClickDelete}>
                    <Box display="flex" flexDirection="row" alignItems={'center'} gap="2" color="fg.error">
                      <LuX />
                      <Text>{t('therapy_sequence.remove')}</Text>
                    </Box>
                  </Menu.Item>
                )}
              </Menu.Content>
            </Menu.Positioner>
          </Portal>
        </Menu.Root>
      </Table.Cell>
    </>
  )
})

interface TherapyRowVirtuosoProps {
  row: FlatRow
  isSelected?: boolean
  daySelectionState?: boolean | 'indeterminate'
  onToggleSelection: (shift: boolean) => void
  onToggleDay: () => void
  onRowClick: (event: MouseEvent, therapy: PatientTherapy) => void
  onClickDelete?: (id: string) => void
  onViewDetails: (therapy: PatientTherapy) => void
  onClickDuplicate?: (therapy: PatientTherapy) => void
  patientProtocols: PatientProtocol[]
  onDelete?: (params: {id: string}) => void
  t: TFnType
}

export const TherapyRowVirtuoso = memo(function TherapyRowVirtuoso({
  row,
  isSelected,
  daySelectionState,
  onToggleSelection,
  onToggleDay,
  onRowClick,
  onClickDelete,
  onViewDetails,
  onClickDuplicate,
  patientProtocols,
  onDelete,
  t,
}: TherapyRowVirtuosoProps) {
  if (row.type === 'date-header') {
    return (
      <>
        <Table.Cell
          paddingBlock="1"
          border="none"
          bg={row.isCurrent ? 'cyan.subtle' : 'none'}
          borderLeft={row.isCurrent ? '2px solid #0891B2' : 'none'}
        >
          {/* a shift-click is left to the row, which selects the range; the box's own toggle is cancelled */}
          <Box
            onClickCapture={event => {
              if (event.shiftKey) event.preventDefault()
            }}
            onClick={event => {
              if (!event.shiftKey) event.stopPropagation()
            }}
          >
            <Checkbox.Root
              size="md"
              aria-label={t('select_day')}
              checked={daySelectionState ?? false}
              onCheckedChange={() => onToggleDay()}
            >
              <Checkbox.HiddenInput />
              <Checkbox.Control />
            </Checkbox.Root>
          </Box>
        </Table.Cell>
        <Table.Cell
          border="none"
          paddingInline={3}
          paddingBlock={1}
          colSpan={10}
          bg={row.isCurrent ? 'cyan.subtle' : 'none'}
        >
          <Box paddingInline="3" display="flex" alignItems="center" gap="2" justifyContent="space-between">
            <Badge
              bg={row.isCurrent ? 'cyan.subtle' : 'bg.muted'}
              color="white"
              paddingLeft={row.isCurrent ? '1' : '3'}
              paddingRight="3"
              paddingBlock="1"
              borderWidth="1px"
              borderColor={row.isCurrent ? 'cyan.focusRing' : 'border.muted'}
              borderRadius={'50px'}
            >
              {row.isCurrent && (
                <Badge bg="cyan.fg" color="white" borderRadius={'50px'}>
                  {t('current_visit')}
                </Badge>
              )}
              <Text fontSize="sm" fontWeight="medium" lineHeight="20px" color={row.isCurrent ? 'cyan.fg' : 'fg'}>
                {formatDateKey(Number(row.dateKey), Tolgee.locale)}
              </Text>
            </Badge>
          </Box>
        </Table.Cell>
      </>
    )
  }

  if (row.type === 'note') {
    return (
      <NoteTherapyRow
        key={row.therapy.id}
        therapy={row.therapy}
        timeLabel={row.timeLabel}
        isCurrent={row.isCurrent}
        isSelected={isSelected}
        onToggleSelection={onToggleSelection}
        patientProtocols={patientProtocols}
        onRowClick={onRowClick}
        onDelete={onDelete}
        t={t}
      />
    )
  }

  if (row.type === 'medicine-note') {
    return (
      <>
        <Table.Cell
          paddingBlock="1"
          border="none"
          bg={'bg.warning'}
          borderLeft={row.isCurrent ? '2px solid #0891B2' : 'none'}
        ></Table.Cell>
        <Table.Cell paddingBlock="1" border="none" colSpan={10} bg={'bg.warning'}>
          <Box display={'flex'} flexDirection={'row'} gap={2}>
            <LuQuote color="black" />
            <Text fontWeight="medium" fontStyle="italic" fontSize="sm" color="fg.muted">
              {row.notes}
            </Text>
          </Box>
        </Table.Cell>
      </>
    )
  }

  return (
    <NewTherapyRow
      row={row}
      isSelected={isSelected}
      onToggleSelection={onToggleSelection}
      onRowClick={onRowClick}
      onClickDelete={onClickDelete}
      onViewDetails={onViewDetails}
      onClickDuplicate={onClickDuplicate}
      t={t}
    />
  )
})

interface NewTherapyRowRow {
  type: 'therapy'
  therapy: PatientTherapy
  medicine: PatientTherapy['medicine'][number]
  medicineIndex: number
  timeLabel?: string | null
  hasObservations?: boolean
  overdosePreventionLevel?: OverdosePreventionLevel
  limit?: string
  medication?: string
  medicationAmount?: string
  medicationProportion?: string
  solution?: string
  solutionVolume?: string
  steps?: TherapyStep[]
  isCurrent: boolean
}

interface NewTherapyRowProps {
  row: NewTherapyRowRow
  isSelected?: boolean
  onToggleSelection?: (shift: boolean) => void
  onRowClick?: (event: MouseEvent, therapy: PatientTherapy) => void
  onClickDelete?: (id: string) => void
  onViewDetails?: (therapy: PatientTherapy) => void
  onClickDuplicate?: (therapy: PatientTherapy) => void
  t: TFnType
}

interface TherapyRowMenuItemsProps {
  therapy: PatientTherapy
  onViewDetails?: (therapy: PatientTherapy) => void
  onClickDuplicate?: (therapy: PatientTherapy) => void
  onClickDelete?: (id: string) => void
  t: TFnType
}

function TherapyRowMenuItems({therapy, onViewDetails, onClickDuplicate, onClickDelete, t}: TherapyRowMenuItemsProps) {
  return (
    <>
      <Menu.Item
        value="new-txt"
        onClick={e => {
          e.stopPropagation()
          return onViewDetails && onViewDetails(therapy)
        }}
      >
        <Box display="flex" flexDirection="row" alignItems={'center'} gap="2">
          <LuPen />
          <Text>{t('therapy_sequence.view_details')}</Text>
        </Box>
      </Menu.Item>
      {onClickDuplicate && (
        <Menu.Item
          value="new-file"
          onClick={e => {
            e.stopPropagation()
            return onClickDuplicate(therapy)
          }}
        >
          <Box display="flex" flexDirection="row" alignItems={'center'} gap="2">
            <LuCopy />
            <Text>{t('therapy_sequence.duplicate')}</Text>
          </Box>
        </Menu.Item>
      )}
      {onClickDelete && (
        <Menu.Item
          value="new-win"
          onClick={e => {
            e.stopPropagation()
            onClickDelete(therapy.id)
          }}
        >
          <Box display="flex" flexDirection="row" alignItems={'center'} gap="2" color="fg.error">
            <LuX />
            <Text>{t('therapy_sequence.remove')}</Text>
          </Box>
        </Menu.Item>
      )}
    </>
  )
}

const NewTherapyRow = memo(function NewTherapyRow({
  row,
  isSelected,
  onToggleSelection,
  onRowClick,
  onClickDelete,
  onViewDetails,
  onClickDuplicate,
  t,
}: NewTherapyRowProps) {
  const icon = getOverDosePreventionIcon(row.overdosePreventionLevel)
  // the actions menu is shown once per prescription
  const hasActions = row.medicineIndex === 0
  // the row (right click) and the ellipsis have their own menus; opening one closes the other
  const [contextMenuOpen, setContextMenuOpen] = useState(false)
  const [buttonMenuOpen, setButtonMenuOpen] = useState(false)
  const menuItems = (
    <TherapyRowMenuItems
      therapy={row.therapy}
      onViewDetails={onViewDetails}
      onClickDuplicate={onClickDuplicate}
      onClickDelete={onClickDelete}
      t={t}
    />
  )

  return (
    <Menu.Root
      open={contextMenuOpen}
      onOpenChange={({open}) => {
        setContextMenuOpen(open)
        if (open) setButtonMenuOpen(false)
      }}
    >
      <Menu.ContextTrigger asChild>
        <Box display="contents" onClick={(event: MouseEvent) => onRowClick?.(event, row.therapy)}>
          <Table.Cell paddingBlock="1" border="none" borderLeft={row.isCurrent ? '2px solid #0891B2' : 'none'}>
            <Box onClick={event => event.stopPropagation()}>
              <Checkbox.Root
                size="md"
                aria-label={t('select_row')}
                checked={Boolean(isSelected)}
                onCheckedChange={() => onToggleSelection?.(false)}
              >
                <Checkbox.HiddenInput />
                <Checkbox.Control />
              </Checkbox.Root>
            </Box>
          </Table.Cell>
          {row.hasObservations ? (
            <Table.Cell paddingBlock="1">
              <Button
                unstyled
                width="100%"
                height="100%"
                cursor="pointer"
                onClick={event => {
                  event.stopPropagation()
                }}
              >
                <Status.Root colorPalette="orange">
                  <Status.Indicator />
                </Status.Root>
              </Button>
            </Table.Cell>
          ) : (
            <Table.Cell paddingBlock="1"></Table.Cell>
          )}
          {row.medicineIndex === 0 ? (
            <Table.Cell paddingBlock="1" border="none">
              <Box display={'flex'} flexDirection={'column'}>
                <Text fontWeight="normal" fontSize="sm" color="fg">
                  {row.therapy.time}
                </Text>
                {row.timeLabel && (
                  <Text fontWeight="medium" fontSize="xs" color={'fg.muted'} lineHeight={'1rem'}>
                    {row.timeLabel}
                  </Text>
                )}
              </Box>
            </Table.Cell>
          ) : (
            <Table.Cell paddingBlock="1" border="none"></Table.Cell>
          )}
          {row.medicineIndex === 0 ? (
            <Table.Cell paddingBlock="1" border="none">
              <TherapyProtocolInfo therapy={row.therapy} t={t} />
            </Table.Cell>
          ) : (
            <Table.Cell paddingBlock="1" border="none"></Table.Cell>
          )}
          <Table.Cell paddingBlock="1" border="none">
            <Box display={'flex'} flexDirection={'row'} gap={'2'} alignItems="center">
              {icon && row.overdosePreventionLevel && (
                <Tooltip
                  content={
                    row.limit != null && row.limit !== ''
                      ? `${t(row.overdosePreventionLevel)} - ${row.limit}`
                      : t(row.overdosePreventionLevel)
                  }
                >
                  <Icon as={icon.icon} color={icon.color} />
                </Tooltip>
              )}
              <Text fontWeight="medium" fontSize="sm" color="black" wordBreak="break-all">
                {row.medication}
              </Text>
            </Box>
          </Table.Cell>
          <Table.Cell paddingBlock="1" border="none">
            <Box display={'flex'} flexDirection={'column'}>
              <Text fontWeight="medium" fontSize="sm" color="black" wordBreak="break-all">
                {row.medicationAmount}
              </Text>
              {row.medicationProportion && (
                <Text fontWeight="medium" fontSize="xs" color={'fg.muted'} lineHeight={'1rem'}>
                  {row.medicationProportion}
                </Text>
              )}
            </Box>
          </Table.Cell>
          <Table.Cell paddingBlock="1" border="none">
            <Box display={'flex'} flexDirection={'column'}>
              <Text fontWeight="medium" fontSize="sm" color="black" wordBreak="break-all">
                {row.solution}
              </Text>
              {row.solutionVolume && (
                <Text fontWeight="medium" fontSize="xs" color={'fg.muted'} lineHeight={'1rem'}>
                  {row.solutionVolume}
                </Text>
              )}
            </Box>
          </Table.Cell>
          <Table.Cell paddingBlock="1" border="none">
            {row.medicineIndex === 0 && (
              <Box display={'flex'} flexDirection={'column'}>
                <Text fontWeight="normal" fontSize="sm" color="black">
                  {row.therapy?.applicationForm?.name || t('not_available')}
                </Text>
                {row.therapy.duration ? (
                  <Text fontWeight="medium" fontSize="xs" color={'fg.muted'} lineHeight={'1rem'}>
                    {row.therapy.duration} min
                  </Text>
                ) : null}
              </Box>
            )}
          </Table.Cell>
          <Table.Cell paddingBlock="1" border="none">
            {row.medicineIndex === 0 && (
              <Text color={'fg.muted'} fontSize="sm">
                {row.therapy?.internalId || '-'}
              </Text>
            )}
          </Table.Cell>
          <Table.Cell paddingBlock="1" border="none">
            {row.steps && row.medicineIndex === 0 && <TherapySequenceJourneyTimelineInternal items={row.steps} />}
          </Table.Cell>
          {hasActions ? (
            <Table.Cell paddingBlock="1" border="none">
              <Menu.Root
                open={buttonMenuOpen}
                onOpenChange={({open}) => {
                  setButtonMenuOpen(open)
                  if (open) setContextMenuOpen(false)
                }}
              >
                <Menu.Trigger asChild>
                  <Button size="md" variant="plain" paddingInline="0" onClick={e => e.stopPropagation()}>
                    <LuEllipsis />
                  </Button>
                </Menu.Trigger>
                <Portal>
                  <Menu.Positioner>
                    <Menu.Content>{menuItems}</Menu.Content>
                  </Menu.Positioner>
                </Portal>
              </Menu.Root>
            </Table.Cell>
          ) : (
            <Table.Cell paddingBlock="1" border="none"></Table.Cell>
          )}
        </Box>
      </Menu.ContextTrigger>
      <Portal>
        <Menu.Positioner>
          <Menu.Content>{menuItems}</Menu.Content>
        </Menu.Positioner>
      </Portal>
    </Menu.Root>
  )
})
